import { VEHICLE_OPTIONS } from '@allocab/allocab-commons-shared-types';
import { VehicleOutput } from '@allocab/allocab-driver-service-types';
import { useState } from 'react';

import { Toggle } from '../../components/atomic/Toggle';
import { DatePickerModal } from '../../components/DatePickerModal';
import { usePatchVehicleOptions } from '../../hooks/usePatchVehicleOptions';
import { t } from '../../services/translation/translation';
import { endOfDay, formatDate, isBefore } from '../../utils/date';

type Props = {
  vehicle: VehicleOutput;
};

export const VehicleOptionsForm = ({ vehicle }: Props) => {
  const { mutate: patchVehicleOptions } = usePatchVehicleOptions();
  const [isVisible, setIsVisible] = useState(false);
  const now = Date.now();

  const handleAirportBadgeOn = (airportBadgeValidUntil?: number) => {
    if (airportBadgeValidUntil) {
      patchVehicleOptions({
        id: vehicle.id,
        options: (vehicle.options ?? []).filter(o => o !== VEHICLE_OPTIONS.AIRPORT_BADGE),
        airportBadgeValidUntil,
      });
    }
  };

  const handleAirportBadgeChange = async (v: boolean) => {
    if (vehicle) {
      if (!v) {
        patchVehicleOptions({
          id: vehicle.id,
          options: (vehicle.options ?? []).filter(o => o !== VEHICLE_OPTIONS.AIRPORT_BADGE),
        });
      } else {
        setIsVisible(true);
      }
    }
  };

  const updateOption = (option: VEHICLE_OPTIONS, value: boolean) => {
    const newOptions = [...(vehicle.options ?? []), option].filter(o => (!value ? o !== option : true));

    patchVehicleOptions({ id: vehicle.id, options: newOptions });
  };

  return (
    <div className="inline-flex flex-col gap-4 p-6 bg-white border border-gray-200 rounded-lg shadow dark:bg-gray-800 dark:border-gray-700">
      <h6 className="text-lg font-bold dark:text-white">{t('vehicle-route.options')}</h6>

      <Toggle
        label={t('services.airport-badge')}
        value={vehicle.airportBadgeValidUntil != null}
        onValueChange={handleAirportBadgeChange}
      />
      {isVisible ? <DatePickerModal onClose={() => setIsVisible(false)} onSave={handleAirportBadgeOn} /> : null}
      {vehicle.airportBadgeValidUntil ? (
        isBefore(now, endOfDay(vehicle.airportBadgeValidUntil)) ? (
          <p className="text-gray-500 dark:text-gray-400 text-xs">
            {t('common.valid-until', { date: formatDate(vehicle.airportBadgeValidUntil, t('date.date-long')) })}
          </p>
        ) : (
          <p className="text-xs text-red-600 font-bold">
            {t('common.invalid-until', { date: formatDate(vehicle.airportBadgeValidUntil, t('date.date-long')) })}
          </p>
        )
      ) : null}
      <Toggle
        label={t('services.tpe')}
        value={!!vehicle.options?.includes(VEHICLE_OPTIONS.TPE)}
        onValueChange={v => updateOption(VEHICLE_OPTIONS.TPE, v)}
      />
      <Toggle
        label={t('services.booster-seat')}
        value={!!vehicle.options?.includes(VEHICLE_OPTIONS.BOOSTER_SEAT)}
        onValueChange={v => updateOption(VEHICLE_OPTIONS.BOOSTER_SEAT, v)}
      />
      <Toggle
        label={t('services.child-seat')}
        value={!!vehicle.options?.includes(VEHICLE_OPTIONS.CHILD_SEAT)}
        onValueChange={v => updateOption(VEHICLE_OPTIONS.CHILD_SEAT, v)}
      />
    </div>
  );
};
